import { booleanAttribute, ChangeDetectorRef, Component, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { IonBackButton, IonSpinner } from '@ionic/angular/standalone';
import { filter } from 'rxjs/operators';

/**
 * Toolbar back control: shows a spinner over the same slot while router navigation
 * is in flight. `ion-back-button` stays mounted (hidden under the spinner) so Ionic
 * can complete the back action. Spinner state follows the router only so it clears
 * when navigation is cancelled (e.g. canDeactivate guard).
 */
@Component({
  selector: 'crowdctrl-back-button',
  standalone: true,
  imports: [CommonModule, IonBackButton, IonSpinner],
  templateUrl: './back-button.component.html',
  styleUrl: './back-button.component.css',
})
export class CrowdctrlBackButtonComponent {
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly activeNavigationIds = new Set<number>();
  private navigationPending = false;

  /** Required when the control is shown. */
  @Input({ required: true }) defaultHref!: string;
  /** When true, the control is non-interactive (e.g. page is committing an action before navigation). */
  @Input({ transform: booleanAttribute }) backButtonDisabled = false;
  @Input() backButtonIcon?: string;
  @Input() text?: string;
  /** When set (e.g. `start`), forwarded to `ion-back-button` for toolbar placement. */
  @Input() buttonSlot?: string;

  get slotAttr(): string | null {
    return this.buttonSlot?.trim() ? this.buttonSlot.trim() : null;
  }

  get toolbarPending(): boolean {
    return this.navigationPending;
  }

  constructor() {
    this.router.events
      .pipe(
        filter(
          (e): e is NavigationStart | NavigationEnd | NavigationCancel | NavigationError =>
            e instanceof NavigationStart ||
            e instanceof NavigationEnd ||
            e instanceof NavigationCancel ||
            e instanceof NavigationError,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((e) => {
        if (e instanceof NavigationStart) {
          this.activeNavigationIds.add(e.id);
        } else {
          this.activeNavigationIds.delete(e.id);
        }
        this.navigationPending = this.activeNavigationIds.size > 0;
        this.cdr.markForCheck();
      });
  }
}
